import React from 'react'
import { match } from 'ts-pattern'
import { useLingui } from '@lingui/react/macro'
import { Tooltip } from 'antd'

import Cover from './_components/Cover'
import MusicDescription from './_components/MusicDescription'
import MusicTitle from './_components/MusicTitle'
import MicrophoneSvg from './MicrophoneSvg'

import type { CoverProps } from './_components/Cover'
import type { AudioGlobalMetadata } from '@/hooks/audio/store'
import type { showType } from '@/pages/tad.ai/_components/MusicItem/_components/InternalMusicItem/_components/MusicItemSuccess'

import Duration from '@/pages/tad.ai/_components/AudioPlayer/_components/Duration'
import Avatar from '@/pages/tad.ai/_components/Avatar'


import { useMemo } from 'react';
import { cls } from 'tagged-classnames-free';
export interface MusicProfileProps {
  showTagLabel?: boolean
  data?: Partial<AudioGlobalMetadata> | null
  cover?: CoverProps
  className?: string
  showTypes: showType[]
  inGlobalPlayer?: boolean
  errorMessage?: string | null
  extra?: {
    avatar?: {
      size?: number
      className?: string
    }
  }
}

const MusicProfile: React.FC<MusicProfileProps> = (props) => {
  const {
    showTagLabel,
    data,
    showTypes: propsShowTypes,
    className,
    cover,
    extra,
    inGlobalPlayer,
    errorMessage,
  } = props
  const {
    id,
    title,
    musicType,
    style,
    mood,
    voice,
    duration,
    author,
    lyricsPrompt,
    description,
    lyrics,
    coverAuthor,
  } = data || {}
  const showTypes = useMemo(() => {
    if (propsShowTypes.includes('coverInfo') && !coverAuthor) {
      const showTypes = propsShowTypes.filter((type) => type !== 'coverInfo') //展示cover info就不展示userInfo
      showTypes.push('userInfo')
      return showTypes
    }
    return propsShowTypes
  }, [propsShowTypes, coverAuthor])
  const { t } = useLingui()

  // 兼容app很老的数据才有这个字段
  const labels = [style, mood, voice].filter(Boolean).join(', ')

  return (
    <div
      className={cls`flex ${
        inGlobalPlayer
          ? 'min-w-0 max-w-[180px] md:max-w-[300px] lg:max-w-[500px]'
          : 'min-w-0 flex-1'
      } items-center gap-2 md:gap-4 ${className}`}
    >
      <Cover {...cover} />
      <div className='flex min-w-0 flex-1 flex-col justify-center'>
        <MusicTitle
          id={id}
          error={showTypes.includes('error')}
          inGlobalPlayer={inGlobalPlayer}
          musicType={showTagLabel ? musicType : undefined}
        >
          {title}
        </MusicTitle>
        {showTypes.map((type, index) => {
          if (type === 'error') {
            const displayMessage = errorMessage || t`Generation Failed`
            return (
              <div key={index} className={cls`flex w-full items-center`}>
                <Tooltip
                  title={
                    errorMessage ? (
                      <span className='text-f-text-secondary text-sm'>
                        {errorMessage}
                      </span>
                    ) : null
                  }
                  styles={{ root: { maxWidth: 'min(468px, 90vw)' } }}
                >
                  <span
                    className={cls`line-clamp-1 bg-[linear-gradient(90.58deg,#FF3466_0%,#FF4BF3_47.92%,#8635FF_100%)] bg-clip-text text-xs text-transparent`}
                  >
                    {displayMessage}
                  </span>
                </Tooltip>
              </div>
            )
          } else {
            return (
              <MusicDescription key={index} className={cls`mb-[5px] !text-xs`}>
                {match(type)
                  .with('prompt', () => lyricsPrompt)
                  .with('labels', () =>
                    musicType === 'Tts'
                      ? lyrics
                      : lyricsPrompt || description || labels,
                  )
                  .with('userInfo', () => (
                    <div
                      className={cls`flex min-w-0 items-center gap-1 [&_img]:size-full [&_img]:object-cover`}
                    >
                      <Avatar
                        className={extra?.avatar?.className}
                        size={extra?.avatar?.size || 16}
                        url={author?.avatar}
                        name={author?.displayName || ''}
                      />
                      <span className='min-w-0 truncate'>
                        {author?.displayName}
                      </span>
                      {showTypes.includes('duration') ? (
                        <Duration
                          className='border-f-border border-l pl-1'
                          value={duration}
                        />
                      ) : null}
                    </div>
                  ))
                  .with('coverInfo', () => (
                    <div
                      className={cls`flex min-w-0 items-center gap-1 [&_img]:size-full [&_img]:object-cover`}
                    >
                      {showTypes.includes('duration') ? (
                        <Duration
                          className='border-f-border border-r pr-1'
                          value={duration}
                        />
                      ) : null}
                      <MicrophoneSvg className='size-4' />
                      <Avatar
                        className={extra?.avatar?.className}
                        size={extra?.avatar?.size || 16}
                        url={coverAuthor?.image}
                        name={coverAuthor?.name || ''}
                      />
                      <span className='min-w-0 flex-1 truncate'>
                        {coverAuthor?.name}
                      </span>
                    </div>
                  ))
                  .with('duration', () =>
                    showTypes.includes('userInfo') ||
                    showTypes.includes('coverInfo') ? null : (
                      <Duration value={duration} />
                    ),
                  )
                  .otherwise(() => null)}
              </MusicDescription>
            )
          }
        })}
        {/* {!!musicTask?.label && (
            <Tooltip title={musicTask.label}>
              <CssIcon
                className={cls`${musicTask.iconClassName}`}
                containerClassName={cls`
                  bg-f-bg-active text-f-text-quaternary hover:text-f-text-secondary inline-flex size-[22px]
                  rounded align-middle
                  ${tagStr ? 'ml-1' : ''}
                `}
              />
            </Tooltip>
          )} */}
      </div>
    </div>
  )
}

export default MusicProfile
